import { CopyAll, ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Grid, IconButton, Stack } from "@mui/material";
import {SyntheticEvent, useCallback, useEffect, useMemo, useState} from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { cbErrorSectionValidationState, isGlobalEditModeState, isMobilityVoucherModeState, provincesState } from "../state/customerbaseState";
import { CBCountry, CBCreationOfProps, CBGoogleAutocompleteType, CBInvoicingCode, CBYesNoType, IFormValues } from "../types";
import { extractGooglePlaceLocalityLongName, extractGooglePlaceTypeLongName, extractGooglePlaceTypeShortName } from "../utils";
import CBFormTextFieldController from "./formController/CBFormTextFieldController";
import UpdateBtnsAccordion from "./UpdateBtnsAccordion";
import CBCountryController from "./formController/CBCountryController";
import CBProvinceController from "./formController/CBProvinceController";
import CBGoogleAutocompleteSearchBar from "./CBGoogleAutocompleteSearchBar";
import CBAccordionTitle from "./CBAccordionTitle";

const CustomerBaseMainInvoicingAddressDetails = ({creationOf} : CBCreationOfProps) => {

    const { t } = useTranslation('customerbase');
    const isCreationMode = !!creationOf;
    const formContext = useFormContext<IFormValues>();
    const [isExpanded, setIsExpanded] = useState(isCreationMode);
    const isGlobalEditMode = useRecoilValue(isGlobalEditModeState);
    const editing = isGlobalEditMode;
    
    const provinceOptions = useRecoilValue(provincesState);
    const countryWatch = useWatch<IFormValues, "cbPersonalInfo.vatNumberCountry">({name: 'cbPersonalInfo.vatNumberCountry'});
    const addressCountryWatch = useWatch<IFormValues, "cbInvoicingAddress.invoicingCountry">({name: 'cbInvoicingAddress.invoicingCountry'});
    const cbCustomerId = useWatch<IFormValues, 'cbDetails.cbCustomerId'>({name: "cbDetails.cbCustomerId"});
    const setErrorSection = useSetRecoilState(cbErrorSectionValidationState);
    const isMobilityVoucherMode = useRecoilValue(isMobilityVoucherModeState);
    const provMandatory = useMemo(() => { 
        return (addressCountryWatch === CBCountry.ITALY)},[addressCountryWatch]);

    const pecSdiVisible = useMemo(()=>{
        return ((countryWatch === CBCountry.ITALY && creationOf === "organization") ||
         (creationOf === "person" && addressCountryWatch === CBCountry.ITALY)) ||
         (!creationOf && addressCountryWatch === CBCountry.ITALY)
    },[countryWatch,creationOf,addressCountryWatch])

    const handleCopy = useCallback((e: SyntheticEvent) => {
        e.stopPropagation();
        const cbPersonalInfo = formContext.getValues("cbPersonalInfo");

        const email = formContext.getValues("cbDetails.email");
        formContext.setValue("cbInvoicingAddress.invoicingStreet", cbPersonalInfo.residenceStreet || '', {shouldValidate: true, shouldDirty: true});
        formContext.setValue("cbInvoicingAddress.invoicingHouseNumber", cbPersonalInfo.residenceHouseNumber || '', {shouldValidate: true, shouldDirty: true});
        formContext.setValue("cbInvoicingAddress.invoicingZipCode", cbPersonalInfo.residenceZipCode || '', {shouldValidate: true, shouldDirty: true});
        formContext.setValue("cbInvoicingAddress.invoicingProvince", cbPersonalInfo.residenceProvince || '', {shouldValidate: true, shouldDirty: true});
        formContext.setValue("cbInvoicingAddress.invoicingCity", cbPersonalInfo.residenceCity || '', {shouldValidate: true, shouldDirty: true});
        formContext.setValue("cbInvoicingAddress.invoicingCountry", cbPersonalInfo.residenceCountry || '', {shouldValidate: true, shouldDirty: true});
        formContext.setValue("cbInvoicingAddress.invoicingAddrEmail", email || '', {shouldValidate: true, shouldDirty: true});
        const currentInvoiceCode = formContext.getValues("cbInvoicingAddress.invoicingRecipientCode");

        if(isCreationMode) {
            if(cbPersonalInfo.publicAuthority == CBYesNoType.YES) {
                formContext.setValue("cbInvoicingAddress.invoicingRecipientCode", '', {shouldValidate: true, shouldDirty: true});
            } else if(formContext.getValues("cbInvoicingAddress.invoicingCountry") != CBCountry.FRANCE && (!currentInvoiceCode || currentInvoiceCode == "--"))
                formContext.setValue("cbInvoicingAddress.invoicingRecipientCode", CBInvoicingCode.DEFAULT as string, {shouldValidate: true, shouldDirty: true});
        }

        formContext.trigger("cbInvoicingAddress").then((isValid) => {
            setErrorSection((prev) => {
                return {...prev, ["cbInvoicingAddress"]: !isValid}
            });
        });
    }, [formContext, isCreationMode]);


    return (
        <Accordion expanded={isExpanded} onChange={(_e, expanded) => {
            setIsExpanded(expanded);
        }}>
            <AccordionSummary expandIcon={ <ExpandMore />}>
                {isCreationMode ? 
                    <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                        <CBAccordionTitle
                            section={!!cbCustomerId && isCreationMode ? undefined : "cbInvoicingAddress"}
                            label={ !!cbCustomerId ? t("tabs.accordionsLabels.cbMainAddress") : t("tabs.accordionsLabels.cbInvoicingAddress") }
                        />
                        <IconButton sx={{mr: 2}} onClick={handleCopy}>
                            <CopyAll fontSize="small"/>
                        </IconButton>
                    </Stack>
                :
                    <UpdateBtnsAccordion
                        label={t("tabs.accordionsLabels.cbMainAddress")}
                        editing={editing}
                        isExpanded={isExpanded}
                        showCustomIcon={editing}
                        customIcon={<CopyAll fontSize="small"/>}
                        onClickCustomIcon={handleCopy}
                    />
                }
            </AccordionSummary>
            <AccordionDetails> 
                <Grid container columnSpacing={3} rowSpacing={1}>
                    {(isCreationMode || editing) && <Grid item md={12} xs={12}>
                        <CBGoogleAutocompleteSearchBar 
                            idField="cb-invoicing-google-autocomplete-search-bar"
                            onPlaceSelected={(place) => {
                                console.log(place);

                                const country = extractGooglePlaceTypeShortName(place, CBGoogleAutocompleteType.COUNTRY);
                            
                                if(!!country && country == CBCountry.ITALY) {
                                    const province = extractGooglePlaceTypeShortName(place, CBGoogleAutocompleteType.PROVINCE);
                                    formContext.setValue("cbInvoicingAddress.invoicingProvince", province || '', {shouldDirty: true, shouldValidate: true});
                                }
                                
                                const city = extractGooglePlaceLocalityLongName(place);
                                const postalCode = extractGooglePlaceTypeLongName(place, CBGoogleAutocompleteType.POSTAL_CODE);
                                const streetName = extractGooglePlaceTypeLongName(place, CBGoogleAutocompleteType.ROUTE);
                                const streetNumber = extractGooglePlaceTypeLongName(place, CBGoogleAutocompleteType.STREET_NUMBER);

                                formContext.setValue("cbInvoicingAddress.invoicingCountry", country || '', {shouldDirty: true, shouldValidate: true});
                                formContext.setValue("cbInvoicingAddress.invoicingCity", city || '', {shouldDirty: true, shouldValidate: true});
                                formContext.setValue("cbInvoicingAddress.invoicingZipCode", postalCode || '', {shouldDirty: true, shouldValidate: true});
                                formContext.setValue("cbInvoicingAddress.invoicingStreet", streetName || '', {shouldDirty: true, shouldValidate: true});
                                formContext.setValue("cbInvoicingAddress.invoicingHouseNumber", streetNumber || '', {shouldDirty: true, shouldValidate: true});

                                //formContext.trigger("cbInvoicingAddress");
                            }}
                            onChange={(event) => {
                                if(!event.currentTarget.value) {
                                    
                                    formContext.resetField("cbInvoicingAddress.invoicingCountry");
                                    formContext.resetField("cbInvoicingAddress.invoicingProvince");
                                    formContext.resetField("cbInvoicingAddress.invoicingCity");                                   
                                    formContext.resetField("cbInvoicingAddress.invoicingZipCode");
                                    formContext.resetField("cbInvoicingAddress.invoicingStreet");
                                    formContext.resetField("cbInvoicingAddress.invoicingHouseNumber");

                                    formContext.trigger([
                                        "cbInvoicingAddress.invoicingCountry",
                                        "cbInvoicingAddress.invoicingProvince",
                                        "cbInvoicingAddress.invoicingCity",
                                        "cbInvoicingAddress.invoicingZipCode",
                                        "cbInvoicingAddress.invoicingStreet",
                                        "cbInvoicingAddress.invoicingHouseNumber"
                                    ]);
                                }
                            }}
                        />
                        </Grid>}
                    <Grid item md={6} xs={12}>
                        {
                            <CBCountryController 
                                formName={"cbInvoicingAddress.invoicingCountry"}
                                idField={"invoicing-country"}
                                label={"country"}
                                //readOnly={isCreationMode ? false : !editing}
                                showSelect={isCreationMode || editing}
                                handleBlankedValueOnChange={() => {
                                    formContext.setValue("cbInvoicingAddress.invoicingProvince", '');
                                }}
                                handleAdditionalBehaviourOnBlur={() => {
                                    formContext.trigger("cbInvoicingAddress.invoicingProvince");
                                }}
                            />
                        }
                    </Grid> 
                    <Grid item md={6} xs={12}>
                        {
                            
                            <CBProvinceController
                                key={"invoicing-province-controller"}
                                formName={"cbInvoicingAddress.invoicingProvince"}
                                idField={"invoicing-province"}
                                label={"province"}
                                provinceOptions={provinceOptions.filter(p => p.country == addressCountryWatch).map(p => ({
                                   key: p.codeProvince,
                                   value: p.description 
                                }))}
                                showSelect={isCreationMode || editing}
                                isMandatory={isMobilityVoucherMode || provMandatory}
                            />
                        }
                    </Grid>
                    <Grid item md={6} xs={12}>
                        {<CBFormTextFieldController 
                            formName={"cbInvoicingAddress.invoicingCity"}
                            idField={"invoicing-city"}
                            label={"city"}
                            readOnly={isCreationMode ? false : !editing}
                            required={isMobilityVoucherMode}
                        />}
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={"cbInvoicingAddress.invoicingZipCode"}
                            idField={"invoicing-zipcode"}
                            label={"zipCode"}
                            readOnly={isCreationMode ? false : !editing}
                            required={isMobilityVoucherMode}
                        />
                    </Grid>         
                    <Grid item md={6} xs={12}>
                        {<CBFormTextFieldController 
                            formName={"cbInvoicingAddress.invoicingStreet"}
                            idField={"invoicing-street"}
                            label={"street"}
                            readOnly={isCreationMode ? false : !editing}
                            required={isMobilityVoucherMode}
                        />}
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={"cbInvoicingAddress.invoicingHouseNumber"}
                            idField={"invoicing-housenumber"}
                            label={"houseNumber"}
                            readOnly={isCreationMode ? false : !editing}
                            required={isMobilityVoucherMode}
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={"cbInvoicingAddress.invoicingAddrEmail"}
                            idField={"invoicing-addrEmail"}
                            label={"addrEmail"}
                            readOnly={isCreationMode ? false : !editing}
                            required={isMobilityVoucherMode}
                        />
                    </Grid>
                    {pecSdiVisible &&  
                        <>
                        
                            <Grid item md={6} xs={12}>
                                <CBFormTextFieldController 
                                    formName={"cbInvoicingAddress.invoicingPec"}
                                    idField={"invoicing-pec"}
                                    label={"pec"}
                                    readOnly={isCreationMode ? false : !editing}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <CBFormTextFieldController 
                                    formName={"cbInvoicingAddress.invoicingRecipientCode"}
                                    idField={"invoicing-recipientCode"}
                                    label={"recipientCode"}
                                    inputProps={{maxLength: 7}}
                                    readOnly={isCreationMode ? false : !editing}
                                    required={isMobilityVoucherMode}
                                    
                                />
                            </Grid>
                        </>
                    }
                    {/*7/4/23: SI NASCONDE*/
                    /*<Grid item md={6} xs={12}>
                        <Controller 
                            control={control}
                            name={"cbInvoicingAddress.invoicingAddrMobile"}
                            render={({field, fieldState}) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    InputProps={{
                                        readOnly: isCreationMode ? false : !editing
                                    }}
                                    id="invoicing-addrMobile"
                                    label={t("addrMobile")}
                                    //type="text"
                                    value={field.value}
                                    onChange={(event) => {
                                        if(event.target.value.match(/(^[0-9]+$|^$|^\s$|^-*$)/))
                                            field.onChange(event.target.value);
                                    }}
                                    error={Boolean(fieldState.error)}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                    </Grid>*/}
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={"cbInvoicingAddress.invoicingTelephone"}
                            idField={"invoicing-telephone"}
                            label={"telephone"}
                            readOnly={isCreationMode ? false : !editing}
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={"cbInvoicingAddress.invoicingFax"}
                            idField={"invoicing-fax"}
                            label={"fax"}
                            readOnly={isCreationMode ? false : !editing}
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={"cbInvoicingAddress.invoicingWebsite"}
                            idField={"invoicing-website"}
                            label={"website"}
                            readOnly={isCreationMode ? false : !editing}
                        />
                    </Grid>
                    {/*30/03/23: AD ORA SI È DECISO DI NON MOSTRARLO*/ 
                    /*<Grid item md={6} xs={12}>
                        <Controller 
                            control={control}
                            name={"cbInvoicingAddress.companyFiscalCode"}
                            render={({field, fieldState}) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    InputProps={{
                                        readOnly: isCreationMode ? false : !editing
                                    }}
                                    id="invoicing-companyFiscalCode"
                                    label={t("companyFiscalCode")}
                                    type="text"
                                    value={field.value}
                                    error={Boolean(fieldState.error)}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                    </Grid>*/}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};

export default CustomerBaseMainInvoicingAddressDetails;